import { utils, write } from 'xlsx';

/**
 * REV-22007 — the "aumico Saldobilanz" v1 template (ADR-0020), built in the browser.
 *
 * Built rather than shipped as a static `.xlsx` on purpose: a binary asset would have to be copied
 * into every app that shows the upload (the wizard in the reports app, the workspace dialog, the
 * component lab), and an asset that is missing in one of them 404s silently — the very failure mode
 * `icon-assets.unit.spec.ts` exists for. SheetJS is already a dependency of the libraries, so the
 * template costs no new package, and generating it keeps the headers in code, next to the parser's
 * aliases they have to match (`parse-trial-balance.ts` in `aumico-be`).
 *
 * The example rows balance — closing AND opening — so a trustee who uploads the untouched template
 * sees the preview, not a refusal.
 */

export const TRIAL_BALANCE_TEMPLATE_FILE_NAME = 'aumico-saldobilanz-vorlage.xlsx';

/** The header row. German, like the refusals name them; French and English headers are accepted too. */
export const TRIAL_BALANCE_TEMPLATE_HEADER = ['Konto', 'Bezeichnung', 'Eröffnungssaldo', 'Saldo'] as const;

/** Three balanced example accounts: debit positive, credit negative. */
const EXAMPLE_ROWS: readonly (readonly (number | string)[])[] = [
    ['1020', 'Bank', 10_000, 12_500],
    ['2000', 'Verbindlichkeiten aus Lieferungen und Leistungen', 0, -2_500],
    ['2800', 'Aktienkapital', -10_000, -10_000],
];

/** The notes sheet — the format rules, in the words the refusals use. */
const NOTES: readonly (readonly string[])[] = [
    ['aumico Saldobilanz — Format v1'],
    [''],
    ['Blatt «Saldobilanz» (sonst das erste Blatt), Kopfzeile in Zeile 1.'],
    ['Konto: Pflicht, 1–20 Zeichen (Ziffern, Buchstaben, Punkt, Bindestrich), eindeutig.'],
    ['Bezeichnung: Pflicht, höchstens 200 Zeichen.'],
    ['Saldo: Schlusssaldo per Stichtag, Soll positiv, Haben negativ. Alternativ zwei Spalten Soll und Haben (Saldo = Soll − Haben).'],
    ['Eröffnungssaldo: freiwillig, gleiche Vorzeichen.'],
    ['Beträge in CHF, z. B. 1\'234.50, -1234,50 oder (1234.50).'],
    ['Die Summe aller Salden muss 0 ergeben (Toleranz 0.01 CHF), sonst wird die Datei abgewiesen.'],
    ['Zeilen ohne Kontonummer (Zwischensummen, Total) werden übersprungen und angezeigt.'],
    ['Höchstens 5 000 Konten, Datei höchstens 5 MB, nur .xlsx.'],
];

/** The template's bytes. Pure — no DOM — so it is testable in Node. */
export function buildTrialBalanceTemplate(): ArrayBuffer {
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, utils.aoa_to_sheet([[...TRIAL_BALANCE_TEMPLATE_HEADER], ...EXAMPLE_ROWS.map((row) => [...row])]), 'Saldobilanz');
    utils.book_append_sheet(workbook, utils.aoa_to_sheet(NOTES.map((row) => [...row])), 'Hinweise');

    return write(workbook, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
}

/**
 * Hands the template to the browser as a download. The one DOM-touching line of this feature, kept
 * out of the pure views: they emit `templateRequested`, and the page that owns the screen calls this.
 */
export function downloadTrialBalanceTemplate(doc: Document = document): void {
    const blob = new Blob([buildTrialBalanceTemplate()], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const anchor = doc.createElement('a');
    anchor.href = url;
    anchor.download = TRIAL_BALANCE_TEMPLATE_FILE_NAME;
    doc.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Revoked on the next tick: some browsers start the download asynchronously after `click()`.
    setTimeout(() => URL.revokeObjectURL(url), 0);
}
